import type { UnactivatedMember } from "@storyboard/web-client";
import {
  DRIVER_SAMSARA_USERNAME_SEARCH_TAG_LABEL,
  DRIVER_SAMSARA_VEHICLE_NAME_SEARCH_TAG_LABEL,
  type DriverSearchTarget,
} from "@/types/driver";

function tagValuesMatchLabel(
  tagValues: Record<string, string[]> | null | undefined,
  tagDefinitions: readonly { id: string; label: string }[] | undefined,
  label: string,
  q: string,
): boolean {
  if (tagDefinitions == null || tagDefinitions.length === 0) return false;
  const ids = tagDefinitions.filter((t) => t.label === label).map((t) => t.id);
  if (ids.length === 0) return false;
  return ids.some((id) => (tagValues?.[id] ?? []).some((v) => v.toLowerCase().includes(q)));
}

/**
 * Mirrors SQL-backed driver search for client-only synthetic never-logged-in rows.
 *
 * Core columns other than `name` have no local data on `UnactivatedMember`. Tag targets consult optional
 * `tagValues` when the membership has synced tag rows locally. `samsaraUsername` / `samsaraVehicleName`
 * consult `tagDefinitions` to resolve tag ids by label (same labels as the driver repository SQL).
 */
export function unactivatedMemberMatchesSearch(
  m: UnactivatedMember,
  search: string,
  searchTargets: readonly DriverSearchTarget[],
  tagValues?: Record<string, string[]> | null,
  tagDefinitions?: readonly { id: string; label: string }[],
): boolean {
  const q = search.trim().toLowerCase();
  if (q.length === 0) return true;
  if (searchTargets.length === 0) return false;

  for (const target of searchTargets) {
    if (target.kind === "column" && target.field === "name") {
      const fields = [
        m.account?.name,
        m.account?.email,
        m.account?.phoneNumber,
        m.invite?.name,
        m.invite?.email,
        m.invite?.phoneNumber,
      ];
      if (fields.some((f) => f != null && f.toLowerCase().includes(q))) return true;
      continue;
    }
    if (target.kind === "column" && target.field === "samsaraUsername") {
      if (tagValuesMatchLabel(tagValues, tagDefinitions, DRIVER_SAMSARA_USERNAME_SEARCH_TAG_LABEL, q)) {
        return true;
      }
      continue;
    }
    if (target.kind === "column" && target.field === "samsaraVehicleName") {
      if (tagValuesMatchLabel(tagValues, tagDefinitions, DRIVER_SAMSARA_VEHICLE_NAME_SEARCH_TAG_LABEL, q)) {
        return true;
      }
      continue;
    }
    if (target.kind === "column") {
      continue;
    }
    const vals = tagValues?.[target.tagId];
    if (vals?.some((v) => v.toLowerCase().includes(q))) return true;
  }
  return false;
}
